import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { fetchPlaybooksPage, playbookKeys } from '../api/queries/PlaybookQueries'
import type { SectionId } from '../components/content-manager/ContentSidebar'

// Sections that are visible on initial load (primary content tabs)
const VISIBLE_SECTIONS: SectionId[] = ['playbooks', 'documents']
// Sections that are below the fold or less frequently used
const DEFERRED_SECTIONS: SectionId[] = ['recent', 'shared', 'starred', 'trash']

function prefetchSectionQueries(
	queryClient: ReturnType<typeof useQueryClient>,
	teamId: string,
	section: SectionId,
) {
	// Prefetch playbooks (uses same key as main hook = cache hit!)
	queryClient.prefetchQuery({
		queryKey: playbookKeys.list(teamId, section, null),
		queryFn: () =>
			fetchPlaybooksPage(20, undefined, section, null, teamId).then(result => ({
				items: result.playbooks,
				nextCursor: result.nextCursor,
				hasMore: result.hasMore,
			})),
	})

	// Documents are block-based - prefetch handled by useBlocksData
}

export function usePrefetchSections(teamId: string | null) {
	const queryClient = useQueryClient()

	// Progressive prefetch: visible sections immediately, others deferred to idle time
	useEffect(() => {
		if (!teamId) return

		// Prefetch visible sections immediately
		for (const section of VISIBLE_SECTIONS) {
			prefetchSectionQueries(queryClient, teamId, section)
		}

		// Defer non-visible sections to idle time
		if (typeof requestIdleCallback === 'function') {
			const idleCallbackId = requestIdleCallback(() => {
				for (const section of DEFERRED_SECTIONS) {
					prefetchSectionQueries(queryClient, teamId, section)
				}
			})
			return () => cancelIdleCallback(idleCallbackId)
		} else {
			// Fallback for environments without requestIdleCallback
			const timerId = setTimeout(() => {
				for (const section of DEFERRED_SECTIONS) {
					prefetchSectionQueries(queryClient, teamId, section)
				}
			}, 200)
			return () => clearTimeout(timerId)
		}
	}, [teamId, queryClient])

	// Hover prefetch function for on-demand prefetching
	const prefetchSection = useCallback((section: SectionId) => {
		if (!teamId) return
		prefetchSectionQueries(queryClient, teamId, section)
	}, [teamId, queryClient])

	return { prefetchSection }
}
